"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function GenerationsOfCraftsmanship() {
  const {
    ref: eyebrowRef,
    isVisible: eyebrowVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: headlineRef,
    isVisible: headlineVisible
  } = useRevealOnScroll<HTMLHeadingElement>({
    threshold: 0.2
  });
  const {
    ref: para1Ref,
    isVisible: para1Visible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const {
    ref: para2Ref,
    isVisible: para2Visible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const {
    ref: para3Ref,
    isVisible: para3Visible
  } = useRevealOnScroll<HTMLParagraphElement>({
    threshold: 0.2
  });
  const {
    ref: ctaRef,
    isVisible: ctaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  const {
    ref: imageRef,
    isVisible: imageVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const headlineWords = ["WE", "DON'T", "BUILD.", "WE", "LEGACY."];
  return <section data-section-id="3957" id="generations-of-craftsmanship" className="bg-background text-foreground py-32 md:py-44 lg:py-[180px] px-6 md:px-12 lg:px-16 overflow-hidden">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8">
          {}
          <div className="lg:col-span-5">
            <div className="lg:sticky lg:top-24">
              <div ref={imageRef} className={`relative transition-all duration-1000 ease-out ${imageVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
              filter: "drop-shadow(0px 40px 80px hsl(38 95% 20% / 0.15))"
            }}>
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/moscariello_construction_site.webp" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/moscariello_construction_site.webp-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1723461/img/moscariello_construction_site.webp-wvc-sizes" alt="Americo Moscariello overseeing a high-end construction project in Collegeville" loading="lazy" decoding="async" className="w-full aspect-[3/4] object-cover" />
              </div>
            </div>
          </div>

          {}
          <div className="lg:col-span-6 lg:col-start-7">
            {}
            <div ref={eyebrowRef} className={`flex items-center gap-3 mb-12 md:mb-16 transition-all duration-700 ease-out ${eyebrowVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <span className="inline-block w-2 h-2 bg-secondary" aria-hidden="true" />
              <span className="font-mono text-[12px] uppercase tracking-[0.2em] text-muted-foreground"><br /></span>
            </div>

            {}
            <h2 ref={headlineRef} className="font-default font-normal uppercase leading-[0.95] tracking-tighter text-foreground text-5xl sm:text-6xl md:text-7xl lg:text-[88px] mb-14 md:mb-16">
              <span className="block">
                {headlineWords.slice(0, 2).map((word, i) => <span key={i} data-index={i} className="inline-block overflow-hidden align-bottom mr-[0.25em]">
                    <span className="inline-block transition-all ease-out" style={{
                  transitionDuration: "800ms",
                  transitionDelay: `${i * 80}ms`,
                  transform: headlineVisible ? "translateY(0)" : "translateY(100%)",
                  opacity: headlineVisible ? 1 : 0
                }}>
                      {word}
                    </span>
                  </span>)}
              </span>
              <span className="block">
                <span className="inline-block overflow-hidden align-bottom mr-[0.25em]">
                  <span className="inline-block transition-all ease-out" style={{
                  transitionDuration: "800ms",
                  transitionDelay: "160ms",
                  transform: headlineVisible ? "translateY(0)" : "translateY(100%)",
                  opacity: headlineVisible ? 1 : 0
                }}>
                    BUILD
                  </span>
                </span>
                <span className="inline-block transition-opacity duration-700 text-secondary" style={{
                transitionDelay: "240ms",
                opacity: headlineVisible ? 1 : 0
              }}>
                  .
                </span>
              </span>
              <span className="block">
                {headlineWords.slice(3, 4).map((word, i) => <span key={`r-${i}`} data-index={i} className="inline-block overflow-hidden align-bottom mr-[0.25em]">
                    <span className="inline-block transition-all ease-out" style={{
                  transitionDuration: "800ms",
                  transitionDelay: "320ms",
                  transform: headlineVisible ? "translateY(0)" : "translateY(100%)",
                  opacity: headlineVisible ? 1 : 0
                }}>
                      {word}
                    </span>
                  </span>)}
                <span className="inline-block overflow-hidden align-bottom">
                  <span className="inline-block transition-all ease-out" style={{
                  transitionDuration: "800ms",
                  transitionDelay: "400ms",
                  transform: headlineVisible ? "translateY(0)" : "translateY(100%)",
                  opacity: headlineVisible ? 1 : 0
                }}>
                    LEGACY
                  </span>
                </span>
                <span className="inline-block transition-opacity duration-700 text-secondary" style={{
                transitionDelay: "480ms",
                opacity: headlineVisible ? 1 : 0
              }}>
                  .
                </span>
              </span>
            </h2>

            {}
            <div className="max-w-xl">
              <p ref={para1Ref} className={`font-default text-[18px] leading-[1.7] text-foreground/85 transition-all duration-700 ease-out ${para1Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                At Moscariello Construction Co, Inc, we believe that every structure is a testament to the values of its owner. We don't just manage properties; we curate environments that stand the test of time.
              </p>

              <div className="h-px w-[60px] my-8 bg-border" aria-hidden="true" />

              <p ref={para2Ref} className={`font-default text-[18px] leading-[1.7] text-foreground/85 transition-all duration-700 ease-out ${para2Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                Founded by Americo Moscariello, our firm brings decades of precision to the Collegeville area. We bridge the gap between complex architectural vision and the reality of durable, high-performance construction.
              </p>

              <div className="h-px w-[60px] my-8 bg-border" aria-hidden="true" />

              <p ref={para3Ref} className={`font-default text-[18px] leading-[1.7] text-foreground/85 transition-all duration-700 ease-out ${para3Visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
                From initial blueprint to final inspection, our commitment is to absolute structural integrity. We are the stewards of your investment, ensuring every detail reflects the quality you expect.
              </p>
            </div>

            {}
            <div ref={ctaRef} className={`mt-14 md:mt-16 transition-all duration-700 ease-out ${ctaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
              <Button asChild variant="ghost" className="group relative h-auto px-0 py-3 bg-transparent hover:bg-transparent text-foreground hover:text-foreground font-mono text-[14px] uppercase tracking-[0.18em] font-medium">
                <Link to="/TheMoscarielloLegacyPage" className="inline-flex items-center gap-3">
                  <span className="relative inline-block">
                    Explore Our Heritage
                    <span className="absolute left-0 -bottom-1 h-px w-0 group-hover:w-full transition-all duration-500 ease-out bg-secondary" aria-hidden="true" />
                  </span>
                  <ArrowRight className="size-4 transition-transform duration-500 ease-out group-hover:translate-x-2 text-secondary" />
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>;
}